import { useState, useEffect } from "react";
import PageHeading from "../PageHeading";
import DynamicSelect from "../DynamicSelect";
import ExportOptions from "../ExportOptions";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { LuLoaderPinwheel } from "react-icons/lu";
import { FaCalendar, FaEye, FaWallet } from "react-icons/fa";
import { FaArrowTrendUp, FaArrowTrendDown } from "react-icons/fa6";
import { ImCross } from "react-icons/im";
import { Link } from "react-router-dom";
import Pagination from "../Shared/Pagination";

const API_ORIGIN = "https://api.muktihospital.com";
const API_KEY =
  process.env.REACT_APP_API_KEY ||
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const money = (n) => (Number(n) || 0).toFixed(2);
const mapNicuTransactions = (list) => {
  const txs = Array.isArray(list) ? list : [];
  let income = 0;
  let expense = 0;
  let bills = 0;
  for (const tx of txs) {
    const d = (tx.description || "").toLowerCase();
    const deposit = Number(tx.deposit) || 0;
    const exp = Number(tx.expense) || 0;
    if (deposit > 0) {
      if (d.startsWith("nicu bill payment")) {
        income += deposit;
        bills += 1;
      } else if (d.startsWith("x-ray")) {
        income += deposit;
      }
      // "Advance Received" deposits are ignored (already inside bill paid).
    }
    if (exp > 0 && d.startsWith("refer commission")) {
      expense += exp;
    }
  }
  return { bills, income, expense, profit: income - expense };
};

// Hospital uses the office-account-summary transaction list.
// Income = Hospital Payment + Outdoor Bill. Expense = Refer Payout + Office Expense.
// Office Deposit is a fund (not income) and is excluded here for a consistent
// Income/Expense/Profit view across modules.
const mapHospitalTransactions = (list) => {
  const txs = Array.isArray(list) ? list : [];
  let income = 0;
  let expense = 0;
  let bills = 0;
  for (const tx of txs) {
    const d = (tx.description || "").toLowerCase();
    const deposit = Number(tx.deposit) || 0;
    const exp = Number(tx.expense) || 0;
    if (deposit > 0 && (d.startsWith("hospital payment") || d.startsWith("outdoor bill"))) {
      income += deposit;
      bills += 1;
    }
    if (exp > 0 && (d.startsWith("refer payout") || d.startsWith("expense:"))) {
      expense += exp;
    }
    // "Office Deposit" and "Doctor Comm" are ignored.
  }
  return { bills, income, expense, profit: income - expense };
};

// Emergency returns ALL bills (no summary, no date filter), so we filter the
// bills to the selected period on the client and sum them.
// Income = paidAmount, Expense = referAmount (refer payout).
const summarizeEmergencyBills = (bills, from, to) => {
  const list = Array.isArray(bills) ? bills : [];
  let income = 0;
  let expense = 0;
  let count = 0;
  const start = new Date(from + "T00:00:00");
  const end = new Date(to + "T23:59:59");
  for (const b of list) {
    const d = new Date(b.createdAt);
    if (d >= start && d <= end) {
      income += Number(b.paidAmount) || 0;
      expense += Number(b.referAmount) || 0;
      count += 1;
    }
  }
  return { bills: count, income, expense, profit: income - expense };
};

// Store Management has no income. Expense = Wastage loss ONLY.
const summarizeStore = (wastageAssets) => {
  const wastageList = Array.isArray(wastageAssets) ? wastageAssets : [];
  const wastageLoss = wastageList.reduce((sum, a) => sum + (Number(a.totalLoss) || 0), 0);
  return { bills: wastageList.length, income: 0, expense: wastageLoss, profit: -wastageLoss };
};

// One entry per module. To add a module later, add a line here with its
// own `fetchRow` mapper — the table/cards need no other change.
const MODULES = [
  {
    name: "Pathology",
    link: "/accounts/pathology",
    // Pathology has a full income/expense/profit endpoint.
    summaryUrl: (params) => `${API_ORIGIN}/api/pathology-accounts/account-summary?${params}`,
    mapSummary: (s) => {
      const agent = Number(s.totalAgentCommission) || 0;
      const doctor = Number(s.totalDoctorPaid) || 0;
      const performer = Number(s.totalPerformerPaid) || 0;
      const others = Number(s.totalOthersExpense) || 0;
      return {
        bills: Number(s.totalBills) || 0,
        income: Number(s.totalCollected) || 0,
        expense: agent + doctor + performer + others,
        profit: Number(s.netProfit) || 0,
      };
    },
  },
  {
    name: "NICU",
    link: "/accounts/nicu",
    summaryUrl: (params) => `${API_ORIGIN}/api/nicu/office-account-summary?${params}`,
    custom: true,
  },
  {
    name: "Hospital",
    link: "/accounts/hospital",
    summaryUrl: (params) => `${API_ORIGIN}/api/hospital/office-account-summary?${params}`,
    custom: true,
  },
  {
    name: "Pharmacy",
    link: "/accounts/pharmacy",
    summaryUrl: null,
    custom: true,
    isPharmacy: true,
  },
  {
    name: "Emergency",
    link: "/accounts/emergency",
    summaryUrl: () => `${API_ORIGIN}/api/emergency/emergency-bills`,
    custom: true,
  },
  {
    name: "Appointment",
    link: "/accounts/appointment",
    summaryUrl: () => `${API_ORIGIN}/api/appointment`,
    custom: true,
  },
  {
    name: "Ambulance",
    link: "/accounts/ambulance",
    summaryUrl: (params) => {
      const p = new URLSearchParams(params);
      const type = p.get("type") || "monthly";
      return `${API_ORIGIN}/api/ambulance-call/calls/${type}?${params}`;
    },
    custom: true,
  },
  {
    name: "Store",
    link: "/accounts/store",
    summaryUrl: () => `${API_ORIGIN}/api/wastage`,
    custom: true,
  },
];

const TotalRevenue = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  const [filterType, setFilterType] = useState("monthly");
  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [selectedMonth, setSelectedMonth] = useState(today.slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(today.slice(0, 4));

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Accounts" },
    { label: "Total Revenue" },
  ];

  const filterOptions = [
    { value: "daily", label: "Daily" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  const headers = ["Sl", "Module", "Bills", "Income", "Expense", "Net Profit"];

  const dataMapper = (() => {
    let counter = 0;
    return (r) => {
      counter += 1;
      return [counter, r.name, r.bills, money(r.income), money(r.expense), money(r.profit)].join("\t");
    };
  })();

  // Build the query string that account-summary expects (type + dates)
  const buildParams = () => {
    const p = new URLSearchParams({ type: filterType });
    if (filterType === "daily") {
      p.set("startDate", startDate);
      p.set("endDate", endDate);
    } else if (filterType === "monthly") {
      const [year, month] = selectedMonth.split("-");
      p.set("year", year);
      p.set("month", month);
    } else {
      p.set("year", selectedYear);
    }
    return p.toString();
  };

  const currentRange = () => {
    if (filterType === "daily") {
      return { from: startDate, to: endDate };
    } else if (filterType === "monthly") {
      const [year, month] = selectedMonth.split("-");
      const lastDay = new Date(Number(year), Number(month), 0).getDate();
      return { from: `${year}-${month}-01`, to: `${year}-${month}-${String(lastDay).padStart(2, "0")}` };
    }
    return { from: `${selectedYear}-01-01`, to: `${selectedYear}-12-31` };
  };

  const fetchRevenue = async () => {
    setLoading(true);
    setError("");
    try {
      const params = buildParams();

      const jobs = MODULES.map(async (mod) => {
        const base = { name: mod.name, link: mod.link, bills: 0, income: 0, expense: 0, profit: 0 };
        if (mod.custom && mod.name === "Pharmacy") {
          const p = buildParams();
          const type = new URLSearchParams(p).get("type") || "monthly";
          const saleUrl = `${API_ORIGIN}/api/medicine-bill/bills/${type}?${p}`;
          const purchaseUrl = `${API_ORIGIN}/api/medicine-purchase/purchases/${type}?${p}`;
          try {
            const [sRes, pRes] = await Promise.all([
              fetch(saleUrl, { headers: { "Content-Type": "application/json", "x-api-key": API_KEY } }),
              fetch(purchaseUrl, { headers: { "Content-Type": "application/json", "x-api-key": API_KEY } }),
            ]);
            const sData = await sRes.json();
            const pData = await pRes.json();
            const bills = Array.isArray(sData.bills) ? sData.bills : [];
            const purchases = Array.isArray(pData.purchases) ? pData.purchases : [];
            const income = bills.reduce((s, b) => s + (Number(b.paymentAmount) || 0), 0);
            const expense = purchases.reduce((s, pp) => s + (Number(pp.paymentAmount) || 0), 0);
            return { ...base, bills: bills.length, income, expense, profit: income - expense };
          } catch (e) {
            console.error("Pharmacy revenue fetch failed:", e);
            return base;
          }
        }

        // Modules without an endpoint yet stay at 0 (wired later).
        if (!mod.summaryUrl) return base;
        try {
          const res = await fetch(mod.summaryUrl(params), {
            method: "GET",
            headers: { "Content-Type": "application/json", "x-api-key": API_KEY },
          });
          const data = await res.json();
          if (!res.ok) return base;
          if (mod.custom && mod.name === "NICU") {
            return { ...base, ...mapNicuTransactions(data.summary) };
          }
          if (mod.custom && mod.name === "Hospital") {
            return { ...base, ...mapHospitalTransactions(data.summary) };
          }
          if (mod.custom && mod.name === "Emergency") {
            const range = currentRange();
            return { ...base, ...summarizeEmergencyBills(data.bills, range.from, range.to) };
          }
         if (mod.custom && mod.name === "Store") {
            return { ...base, ...summarizeStore(data.assets) };
          }
          if (mod.custom && mod.name === "Appointment") {
            const list = Array.isArray(data.appointments) ? data.appointments : [];
            const range = currentRange();
            const from = new Date(range.from + "T00:00:00");
            const to = new Date(range.to + "T23:59:59");
            const approved = list.filter((a) => {
              if ((a.status || "").toLowerCase() !== "approved") return false;
              const dt = new Date(a.appointmentDate || a.createdAt);
              return dt >= from && dt <= to;
            });
            const income = approved.reduce((s, a) => s + (Number(a.consultationFee) || 0), 0);
            return { ...base, bills: approved.length, income, expense: 0, profit: income };
          }
          if (mod.custom && mod.name === "Ambulance") {
            const calls = Array.isArray(data.calls) ? data.calls : [];
            const income = Number(data.totalAmount) || 0;
            return { ...base, bills: calls.length, income, expense: 0, profit: income };
          }
          if (data.summary) {
            return { ...base, ...mod.mapSummary(data.summary) };
          }
          return base;
        } catch (err) {
          console.error(`Fetch revenue error for ${mod.name}:`, err);
          return base;
        }
      });

      const result = await Promise.all(jobs);
      setRows(result);
      setCurrentPage(1);

      if (result.every((r) => r.bills === 0 && r.income === 0)) {
        toast.warn("No data found for the selected period.", { autoClose: 3000 });
      }
    } catch (err) {
      setError("Unable to connect to the server. Please try again later.");
      console.error("Fetch revenue error:", err);
      toast.error("Failed to fetch revenue data.", { autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRevenue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, startDate, endDate, selectedMonth, selectedYear]);

  // Grand totals across all modules
  const totals = rows.reduce(
    (a, r) => ({
      bills: a.bills + r.bills,
      income: a.income + r.income,
      expense: a.expense + r.expense,
      profit: a.profit + r.profit,
    }),
    { bills: 0, income: 0, expense: 0, profit: 0 }
  );

  const totalPages = Math.ceil(rows.length / limit) || 1;
  const pagedRows = rows.slice((currentPage - 1) * limit, currentPage * limit);
  const handlePageChange = (p) => {
    if (p >= 1 && p <= totalPages) setCurrentPage(p);
  };

  const periodLabel =
    filterType === "daily"
      ? `${startDate} to ${endDate}`
      : filterType === "monthly"
      ? new Date(selectedMonth + "-01").toLocaleString("default", { month: "long", year: "numeric" })
      : selectedYear;

  return (
    <div>
      <PageHeading title="Total Revenue" breadcrumbs={breadcrumbs} />
      <div className="bg-white shadow-sm rounded-lg">
        {/* Filter bar */}
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
        <div className="inline-flex items-center gap-3 shrink-0">
          <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">Total Revenue Dashboard</h2>
        </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <label className="text-sm font-medium text-gray-700 shrink-0">Filter By:</label>
            <div className="w-32 shrink-0">
              <DynamicSelect
                options={filterOptions}
                onChange={(option) => setFilterType(option.value)}
                value={filterOptions.find((opt) => opt.value === filterType)}
                isSearchable={false}
                controlBackgroundColor="#f7fafc"
                controlBorderColor="#cbd5e0"
              />
            </div>
            {filterType === "daily" && (
              <>
                <label className="text-sm font-medium text-gray-700 shrink-0">From:</label>
                <input type="date" className="input-field" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                <label className="text-sm font-medium text-gray-700 shrink-0">To:</label>
                <input type="date" className="input-field" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </>
            )}
            {filterType === "monthly" && (
              <input type="month" className="input-field" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} />
            )}
            {filterType === "yearly" && (
              <input type="number" className="input-field w-28" value={selectedYear} min="2000" max="2099" onChange={(e) => setSelectedYear(e.target.value)} />
            )}
            <ExportOptions
              data={rows}
              title="Total Revenue Report"
              headers={headers}
              dataMapper={dataMapper}
              dropdownOpen={dropdownOpen}
              toggleDropdown={() => setDropdownOpen((p) => !p)}
            />
          </div>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="text-center p-16 text-gray-500 flex flex-col items-center justify-center gap-2">
              <LuLoaderPinwheel className="animate-spin text-2xl" />
              <h3 className="text-lg">Loading revenue data...</h3>
            </div>
          ) : error ? (
            <div className="text-center p-16 text-red-500 flex flex-col items-center justify-center gap-2">
              <ImCross />
              <p className="text-sm">{error}</p>
            </div>
          ) : (
            <>
              <div className="bg-gray-100 p-2.5 mb-5 rounded text-sm text-gray-700 font-medium flex items-center gap-2">
                <FaCalendar /> Showing revenue for: <span className="font-semibold">{periodLabel}</span>
              </div>

              {/* Summary cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {/* Total Income */}
                <div className="relative overflow-hidden bg-white border border-gray-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow duration-300">
                  <span className="absolute left-0 top-0 h-full w-1 bg-emerald-500" />
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Total Income</p>
                      <p className="text-2xl font-bold text-emerald-600 mt-2">৳ {money(totals.income)}</p>
                      <p className="text-xs text-gray-400 mt-1">Cash collected · all modules</p>
                    </div>
                    <div className="size-11 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                      <FaArrowTrendUp size={18} />
                    </div>
                  </div>
                </div>

                {/* Total Expense */}
                <div className="relative overflow-hidden bg-white border border-gray-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow duration-300">
                  <span className="absolute left-0 top-0 h-full w-1 bg-rose-500" />
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Total Expense</p>
                      <p className="text-2xl font-bold text-rose-600 mt-2">৳ {money(totals.expense)}</p>
                      <p className="text-xs text-gray-400 mt-1">Payouts &amp; expenses</p>
                    </div>
                    <div className="size-11 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
                      <FaArrowTrendDown size={18} />
                    </div>
                  </div>
                </div>

                {/* Net Profit */}
                <div className="relative overflow-hidden bg-white border border-gray-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow duration-300">
                  <span className="absolute left-0 top-0 h-full w-1 bg-M-primary-color" />
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Net Profit</p>
                      <p className={`text-2xl font-bold mt-2 ${totals.profit < 0 ? "text-rose-600" : "text-M-heading-color"}`}>
                        ৳ {money(totals.profit)}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">Income − Expense</p>
                    </div>
                    <div className="size-11 rounded-full bg-M-primary-color/10 text-M-primary-color flex items-center justify-center shrink-0">
                      <FaWallet size={18} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="min-w-[700px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left uppercase font-inter font-medium text-sm text-gray-700 p-3">Sl</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-gray-700 p-3">Module</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-gray-700 p-3">Bills</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-green-700 p-3">Income</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-red-700 p-3">Expense</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-blue-700 p-3">Net Profit</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-gray-700 p-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((r, index) => (
                      <tr key={r.name} className="hover:bg-gray-50 border-b">
                        <td className="text-left font-inter text-sm text-gray-700 p-3">{(currentPage - 1) * limit + index + 1}</td>
                        <td className="text-left font-inter text-sm text-gray-700 p-3">{r.name}</td>
                        <td className="text-right font-inter text-sm text-gray-700 p-3">{r.bills}</td>
                        <td className="text-right font-inter text-sm text-green-600 p-3">{money(r.income)}</td>
                        <td className="text-right font-inter text-sm text-red-600 p-3">{money(r.expense)}</td>
                        <td className="text-right font-inter text-sm font-semibold text-blue-700 p-3">{money(r.profit)}</td>
                        <td className="text-right font-inter text-sm p-3">
                          <div className="flex justify-end">
                            <Link
                              to={r.link}
                              className="size-7 rounded-full text-sm flex items-center justify-center bg-blue-600/20 text-blue-600 hover:bg-blue-600 hover:text-white transition-all duration-300"
                            >
                              <FaEye />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}

                    {rows.length > 0 && (
                      <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
                        <td className="text-right font-inter text-sm text-gray-700 p-3" colSpan={2}>Grand Total:</td>
                        <td className="text-right font-inter text-sm text-gray-700 p-3">{totals.bills}</td>
                        <td className="text-right font-inter text-sm text-green-700 p-3">{money(totals.income)}</td>
                        <td className="text-right font-inter text-sm text-red-700 p-3">{money(totals.expense)}</td>
                        <td className="text-right font-inter text-sm text-blue-700 p-3">{money(totals.profit)}</td>
                        <td className="p-3"></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalRecords={rows.length}
                itemsPerPage={limit}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default TotalRevenue;